import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase/client';
import './BitacoraSistema.css';

// ============================================================================
// TIPOS
// ============================================================================

type TablaAfectada = 'pacientes' | 'consultas' | 'terapeutas' | 'pagos';
type AccionBitacora = 'INSERT' | 'UPDATE' | 'DELETE';
type ActorRol = 'director' | 'terapeuta' | 'inversionista' | 'sistema' | 'desconocido';

interface BitacoraRow {
  id: string;
  created_at: string;
  tabla_afectada: TablaAfectada;
  registro_id: string;
  accion: AccionBitacora;
  actor_auth_id: string | null;
  actor_rol: ActorRol | null;
  actor_nombre: string | null;
  cambios: Record<string, { antes: unknown; despues: unknown }> | Record<string, unknown> | null;
}

// ============================================================================
// CONSTANTES
// ============================================================================

const LIMIT = 50;

const pad2 = (n: number) => String(n).padStart(2, '0');

const toISODate = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const defaultFechaDesde = () => toISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
const defaultFechaHasta = () => toISODate(new Date());

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('es-CL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const fmtValue = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Sí' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

// Asignación de color por tabla — criterio de diseño: los 4 tokens --apple-*
// disponibles, evitando que "pagos" quede en rojo (se leería como estado de
// error). "terapeutas" toma el rojo porque son cambios sobre cuentas de staff
// (alta sensibilidad, evento raro) — el rojo ahí funciona como "prestá
// atención", no como "algo falló".
const TABLA_LABEL: Record<TablaAfectada, string> = {
  pacientes: 'Pacientes',
  consultas: 'Consultas',
  terapeutas: 'Terapeutas',
  pagos: 'Pagos',
};

const TABLA_VARIANT: Record<TablaAfectada, ChipVariant> = {
  pacientes: 'blue',
  consultas: 'green',
  terapeutas: 'red',
  pagos: 'orange',
};

const ACCION_LABEL: Record<AccionBitacora, string> = {
  INSERT: 'Creó',
  UPDATE: 'Editó',
  DELETE: 'Eliminó',
};

const ACCION_VARIANT: Record<AccionBitacora, ChipVariant> = {
  INSERT: 'green',
  UPDATE: 'orange',
  DELETE: 'red',
};

const ROL_LABEL: Record<ActorRol, string> = {
  director: 'Director',
  terapeuta: 'Terapeuta',
  inversionista: 'Inversionista',
  sistema: 'Sistema',
  desconocido: 'Desconocido',
};

const ROL_ICON: Record<ActorRol, string> = {
  director: 'fas fa-user-tie',
  terapeuta: 'fas fa-user-md',
  inversionista: 'fas fa-chart-line',
  sistema: 'fas fa-robot',
  desconocido: 'fas fa-question',
};

// ============================================================================
// SUB-COMPONENTES
// ============================================================================

type ChipVariant = 'green' | 'orange' | 'red' | 'blue' | 'gray';

const Chip: React.FC<{ variant: ChipVariant; children: React.ReactNode }> = ({ variant, children }) => (
  <span className={`bitacora-chip bitacora-chip--${variant}`}>{children}</span>
);

const DetalleCambios: React.FC<{ row: BitacoraRow }> = ({ row }) => {
  if (!row.cambios || Object.keys(row.cambios).length === 0) {
    return <p className="bitacora-detalle-empty">Sin detalle disponible.</p>;
  }

  if (row.accion === 'UPDATE') {
    const entries = Object.entries(row.cambios as Record<string, { antes: unknown; despues: unknown }>);
    return (
      <ul className="bitacora-detalle-list">
        {entries.map(([campo, val]) => (
          <li key={campo}>
            <code className="bitacora-detalle-campo">{campo}</code>
            <span className="bitacora-detalle-valor">
              {fmtValue(val?.antes)}
              <i className="fas fa-arrow-right" />
              {fmtValue(val?.despues)}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  const entries = Object.entries(row.cambios as Record<string, unknown>);
  return (
    <ul className="bitacora-detalle-list">
      {entries.map(([campo, val]) => (
        <li key={campo}>
          <code className="bitacora-detalle-campo">{campo}</code>
          <span className="bitacora-detalle-valor">{fmtValue(val)}</span>
        </li>
      ))}
    </ul>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const BitacoraSistema: React.FC = () => {
  const [filtroTabla, setFiltroTabla] = useState<TablaAfectada | 'todas'>('todas');
  const [fechaDesde, setFechaDesde] = useState(defaultFechaDesde);
  const [fechaHasta, setFechaHasta] = useState(defaultFechaHasta);

  const [rows, setRows] = useState<BitacoraRow[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // ── Carga de datos ──────────────────────────────────────────────────────────

  const loadBitacora = async (reset: boolean) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setErrorMsg(null);

    const currentOffset = reset ? 0 : offset;

    const { data, error } = await supabase.rpc('get_bitacora_sistema', {
      p_tabla_afectada: filtroTabla !== 'todas' ? filtroTabla : null,
      p_registro_id: null,
      p_desde: fechaDesde ? new Date(`${fechaDesde}T00:00:00`).toISOString() : null,
      p_hasta: fechaHasta ? new Date(`${fechaHasta}T23:59:59`).toISOString() : null,
      p_limit: LIMIT,
      p_offset: currentOffset,
    });

    if (error) {
      console.error('[BitacoraSistema] Error cargando bitácora:', error.code);
      setErrorMsg('No se pudo cargar la bitácora. Intentá nuevamente.');
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const newRows = (data as BitacoraRow[]) ?? [];
    setRows(prev => (reset ? newRows : [...prev, ...newRows]));
    setHasMore(newRows.length === LIMIT);
    setOffset(currentOffset + newRows.length);
    setLoading(false);
    setLoadingMore(false);
  };

  useEffect(() => {
    loadBitacora(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroTabla, fechaDesde, fechaHasta]);

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const filtrosActivos =
    filtroTabla !== 'todas' || fechaDesde !== defaultFechaDesde() || fechaHasta !== defaultFechaHasta();

  const limpiarFiltros = () => {
    setFiltroTabla('todas');
    setFechaDesde(defaultFechaDesde());
    setFechaHasta(defaultFechaHasta());
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="bitacora-sistema">
      <div className="bitacora-header">
        <div>
          <h2 className="bitacora-title">
            <i className="fas fa-shield-halved" /> Bitácora del Sistema
          </h2>
          <p className="bitacora-subtitle">
            Registro de auditoría — quién creó, editó o eliminó qué
          </p>
        </div>
      </div>

      {/* ── Filtros ── */}
      <div className="bitacora-filters">
        <div className="bitacora-filter-group">
          <label className="bitacora-filter-label">Tabla</label>
          <select
            className="bitacora-input"
            value={filtroTabla}
            onChange={e => setFiltroTabla(e.target.value as TablaAfectada | 'todas')}
          >
            <option value="todas">Todas</option>
            <option value="pacientes">Pacientes</option>
            <option value="consultas">Consultas</option>
            <option value="terapeutas">Terapeutas</option>
            <option value="pagos">Pagos</option>
          </select>
        </div>
        <div className="bitacora-filter-group">
          <label className="bitacora-filter-label">Desde</label>
          <input
            type="date"
            className="bitacora-input"
            value={fechaDesde}
            onChange={e => setFechaDesde(e.target.value)}
          />
        </div>
        <div className="bitacora-filter-group">
          <label className="bitacora-filter-label">Hasta</label>
          <input
            type="date"
            className="bitacora-input"
            value={fechaHasta}
            onChange={e => setFechaHasta(e.target.value)}
          />
        </div>
        {filtrosActivos && (
          <button type="button" className="bitacora-filter-clear" onClick={limpiarFiltros}>
            <i className="fas fa-times" /> Limpiar filtros
          </button>
        )}
      </div>

      {/* ── Error global ── */}
      {errorMsg && (
        <div className="bitacora-error-banner" role="alert">
          <i className="fas fa-exclamation-circle" />
          <span>{errorMsg}</span>
          <button type="button" onClick={() => setErrorMsg(null)} className="bitacora-error-close">
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {/* ── Tabla ── */}
      <div className="bitacora-table-wrapper">
        {loading ? (
          <div className="bitacora-loading">
            <div className="spinner" />
            <p>Cargando bitácora...</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="bitacora-empty">
            <i className="fas fa-inbox" />
            <p>Sin movimientos en este rango.</p>
          </div>
        ) : (
          <>
            <table className="bitacora-table">
              <thead>
                <tr>
                  <th className="bitacora-th--chevron" aria-hidden="true"></th>
                  <th>Fecha y hora</th>
                  <th>Tabla</th>
                  <th>Acción</th>
                  <th>Actor</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const expanded = expandedIds.has(row.id);
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className="bitacora-tr"
                        onClick={() => toggleExpand(row.id)}
                      >
                        <td className="bitacora-td--chevron">
                          <button
                            type="button"
                            className="bitacora-expand-btn"
                            aria-expanded={expanded}
                            aria-label={expanded ? 'Contraer detalle' : 'Expandir detalle'}
                            onClick={e => {
                              e.stopPropagation();
                              toggleExpand(row.id);
                            }}
                          >
                            <i className={`fas fa-chevron-${expanded ? 'down' : 'right'}`} />
                          </button>
                        </td>
                        <td className="bitacora-td--nowrap">{fmtDateTime(row.created_at)}</td>
                        <td>
                          <Chip variant={TABLA_VARIANT[row.tabla_afectada]}>
                            {TABLA_LABEL[row.tabla_afectada]}
                          </Chip>
                        </td>
                        <td>
                          <Chip variant={ACCION_VARIANT[row.accion]}>
                            {ACCION_LABEL[row.accion]}
                          </Chip>
                        </td>
                        <td>
                          <div className="bitacora-actor">
                            <span className="bitacora-actor__nombre">
                              {row.actor_rol === 'sistema' ? 'Sistema' : row.actor_nombre ?? 'Desconocido'}
                            </span>
                            <Chip variant="gray">
                              <i className={ROL_ICON[row.actor_rol ?? 'desconocido']} />{' '}
                              {ROL_LABEL[row.actor_rol ?? 'desconocido']}
                            </Chip>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="bitacora-tr-detail">
                          <td colSpan={5}>
                            <DetalleCambios row={row} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {hasMore && (
              <div className="bitacora-load-more">
                <button
                  type="button"
                  className="bitacora-btn bitacora-btn--ghost"
                  disabled={loadingMore}
                  onClick={() => loadBitacora(false)}
                >
                  {loadingMore ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Cargando...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-chevron-down" /> Cargar más
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BitacoraSistema;
